import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Trash2, RefreshCw, Download, Save, History, FolderOpen, Maximize2, FileText, Calculator } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast, Toaster } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROUND_UNITS = [1, 100, 1000, 10000, 100000];
const CURRENCIES = ['₩', '$', '€', '£', '¥', '₱', 'HK$', '฿'];
const fmt = (n) => Math.round(Number(n) || 0).toLocaleString('en-US');
const num = (v) => Number(String(v).replace(/[^0-9.-]/g, '')) || 0;
const kstToday = () => new Date(Date.now() + 9 * 3600e3).toISOString().slice(0, 10);
const eventLabel = (ev) => (ev.event_name.startsWith('#') || ev.event_name.startsWith(ev.event_number) ? ev.event_name : `#${ev.event_number} ${ev.event_name}`);

const inputCls = 'bg-background-base border-white/10 text-white h-10';
const selectCls = 'h-10 bg-background-base border border-white/10 rounded-md text-white text-sm px-2';
const cellInput = 'w-full min-w-[90px] bg-transparent border-b border-white/10 focus:border-secondary-main outline-none text-white py-2 font-mono text-base';
const numInput = `${cellInput} text-right`;
const card = 'bg-black/30 rounded-lg p-3 sm:p-4';
const h4 = 'font-heading text-xs uppercase tracking-wider text-white/70';

const EventPicker = ({ date, setDate, events, eventId, onPick, onLoad, loading, onRefreshEvents, refreshing }) => (
  <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3" data-testid="icm-event-picker">
    <Input type="date" value={date} onChange={e => setDate(e.target.value)} className={`${inputCls} w-full sm:w-44`} data-testid="icm-date-input" />
    <select value={eventId} onChange={e => onPick(e.target.value)} className={`${selectCls} w-full sm:flex-1 sm:min-w-[260px]`} data-testid="icm-event-select">
      <option value="">{events.length ? '— Manual entry (no event) —' : refreshing ? '— Checking PokerLens for events… —' : '— No events on PokerLens for this date (manual entry) —'}</option>
      {events.map(ev => <option key={ev.pokerlens_id} value={ev.pokerlens_id}>{`${ev.time} · ${eventLabel(ev)}`}</option>)}
    </select>
    <Button variant="ghost" onClick={onRefreshEvents} disabled={refreshing} title="Pull the latest events for this date from PokerLens" className="border border-white/10 text-white/70 h-10 w-full sm:w-auto" data-testid="icm-refresh-events-btn">
      <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Checking…' : 'Refresh events'}
    </Button>
    <Button onClick={onLoad} disabled={!eventId || loading} className="bg-secondary-main text-white h-10 w-full sm:w-auto" data-testid="icm-load-btn">
      <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> {loading ? 'Loading…' : 'Load from PokerLens'}
    </Button>
  </div>
);

const ordinal = (i) => `${i + 1}${['st', 'nd', 'rd'][i] || 'th'}`;

const METHODS = [['icm', 'ICM'], ['chop', 'By chip count'], ['both', 'Both']];

const DealTable = ({ players, setPlayers, payouts, setPayouts, currency, result, hint, method, setMethod }) => {
  const updP = (i, k, v) => setPlayers(players.map((p, j) => (j === i ? { ...p, [k]: v } : p)));
  const updPay = (i, v) => { const next = [...payouts]; while (next.length <= i) next.push(0); next[i] = v; setPayouts(next); };
  const rows = Math.max(players.length, payouts.length);
  const totalChips = players.reduce((s, p) => s + num(p.chips), 0);
  const totalPay = payouts.slice(0, players.length).reduce((s, p) => s + num(p), 0);
  const res = (i) => result?.rows?.[i];
  const th = 'py-2 text-[11px] uppercase tracking-wider text-white/40 font-heading whitespace-nowrap';
  const showIcm = method !== 'chop', showChop = method !== 'icm';
  return (
    <div className={card} data-testid="icm-deal-table-wrap">
      {payouts.length > players.length && players.length > 0 && (
        <p className="text-[11px] text-white/45 mb-2" data-testid="icm-extra-places-note">{payouts.length - players.length} lower payout place{payouts.length - players.length > 1 ? 's' : ''} belong to players already out — only the top {players.length} places are in this deal.</p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h4 className={h4}>Deal sheet <span className="text-white/40">({players.length} players · top {players.length} payouts used)</span></h4>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="inline-flex rounded-md border border-white/10 overflow-hidden" role="group" aria-label="Deal method" data-testid="icm-method-toggle">
            {METHODS.map(([v, label]) => (
              <button key={v} onClick={() => setMethod(v)} className={`px-3 h-9 text-xs font-heading tracking-wider uppercase transition-colors ${method === v ? 'bg-secondary-main text-white' : 'text-white/60 hover:text-white hover:bg-white/5'}`} data-testid={`icm-method-${v}`}>{label}</button>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setPlayers([...players, { name: `Player ${players.length + 1}`, chips: 0 }])} className="border border-white/10 text-white/70 h-9" data-testid="icm-add-player-btn"><Plus className="w-4 h-4 mr-1" /> Player</Button>
          <Button variant="ghost" size="sm" onClick={() => setPayouts([...payouts, 0])} className="border border-white/10 text-white/70 h-9" data-testid="icm-add-payout-btn"><Plus className="w-4 h-4 mr-1" /> Payout place</Button>
        </div>
      </div>
      {hint && <p className="text-[11px] text-amber-300/90 mb-2" data-testid="icm-payouts-hint">{hint}</p>}
      <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full text-sm min-w-[720px]" data-testid="icm-deal-table">
          <colgroup><col className="w-8" /><col /><col className="w-[16%]" /><col className="w-[18%]" />{showIcm && <col className="w-[16%]" />}{showChop && <col className="w-[16%]" />}<col className="w-8" /></colgroup>
          <thead>
            <tr className="border-b border-white/10">
              <th className={`${th} text-left`}>#</th>
              <th className={`${th} text-left`}>Remaining players</th>
              <th className={`${th} text-right`}>Chip count</th>
              <th className={`${th} text-right`}>Remaining payout</th>
              {showIcm && <th className={`${th} text-right text-secondary-main`}>ICM</th>}
              {showChop && <th className={`${th} text-right text-primary-main`}>By chip count</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rows }).map((_, i) => {
              const p = players[i]; const r = res(i);
              return (
                <tr key={i} className="border-b border-white/[0.05]" data-testid={`icm-row-${i}`}>
                  <td className="py-1 text-white/40">{i + 1}</td>
                  <td className="py-1 pr-2">{p ? <input value={p.name} onChange={e => updP(i, 'name', e.target.value)} className={`${cellInput} text-left min-w-[120px]`} data-testid={`icm-player-name-${i}`} /> : <span className="text-white/25 text-xs">—</span>}</td>
                  <td className="py-1">{p ? <input inputMode="numeric" value={p.chips === 0 ? '' : fmt(p.chips)} placeholder="0" onChange={e => updP(i, 'chips', num(e.target.value))} className={numInput} data-testid={`icm-player-chips-${i}`} /> : null}
                    {p && totalChips > 0 && <div className="text-[10px] text-white/35 text-right font-mono">{(100 * num(p.chips) / totalChips).toFixed(1)}%</div>}</td>
                  <td className="py-1"><div className="flex items-center gap-1"><span className="text-[10px] text-white/35 w-7">{ordinal(i)}</span><input inputMode="numeric" value={!payouts[i] ? '' : fmt(payouts[i])} placeholder="0" onChange={e => updPay(i, num(e.target.value))} className={`${numInput} ${i >= players.length ? 'opacity-40' : ''}`} data-testid={`icm-payout-${i}`} /></div></td>
                  {showIcm && <td className="py-1 text-right font-mono text-white font-bold whitespace-nowrap" data-testid={`icm-result-icm-${i}`}>{r ? `${currency} ${fmt(r.icm)}` : <span className="text-white/20">—</span>}</td>}
                  {showChop && <td className="py-1 text-right font-mono text-white font-bold whitespace-nowrap" data-testid={`icm-result-chop-${i}`}>{r ? `${currency} ${fmt(r.chop)}` : <span className="text-white/20">—</span>}</td>}
                  <td className="py-1 text-right">
                    <button onClick={() => { if (p) setPlayers(players.filter((_, j) => j !== i)); if (payouts.length > i) setPayouts(payouts.filter((_, j) => j !== i)); }} className="p-2 text-white/30 hover:text-red-400" title="Remove row" data-testid={`icm-remove-row-${i}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="text-white/80 font-heading text-xs uppercase tracking-wider">
              <td /><td className="py-2">Total</td>
              <td className="py-2 text-right font-mono" data-testid="icm-total-chips">{fmt(totalChips)}</td>
              <td className="py-2 text-right font-mono" data-testid="icm-total-payouts">{currency} {fmt(totalPay)}</td>
              {showIcm && <td className="py-2 text-right font-mono" data-testid="icm-total-icm">{result ? `${currency} ${fmt(result.icm_total)}` : '—'}</td>}
              {showChop && <td className="py-2 text-right font-mono" data-testid="icm-total-chop">{result ? `${currency} ${fmt(result.chop_total)}` : '—'}</td>}<td />
            </tr>
            {result && result.deduction > 0 && (
              <tr className="text-amber-300 font-heading text-xs uppercase tracking-wider border-t border-white/10" data-testid="icm-deduction-row">
                <td /><td className="py-2" colSpan={2}>{result.deduction_mode === 'winner' ? 'Playing amount for 1st (not in the split)' : 'Deducted from pool'}</td>
                <td className="py-2 text-right font-mono" data-testid="icm-deduction-amount">{currency} {fmt(result.deduction)}</td>
                {showIcm && <td />}{showChop && <td />}<td />
              </tr>
            )}
          </tfoot>
        </table>
      </div>
    </div>
  );
};

const DealSettings = ({ deduction, setDeduction, mode, setMode, roundTo, setRoundTo, currency, setCurrency }) => (
  <div className={`${card} grid grid-cols-2 lg:grid-cols-4 gap-3`} data-testid="icm-settings">
    <label className="block col-span-2 sm:col-span-1">
      <span className="font-heading text-[11px] uppercase tracking-wider text-white/50">{mode === 'winner' ? 'Playing amount for 1st' : 'Deducted from pool'}</span>
      <Input inputMode="numeric" value={deduction === 0 ? '' : fmt(deduction)} placeholder="0" onChange={e => setDeduction(num(e.target.value))} className={`${inputCls} mt-1 text-right font-mono`} data-testid="icm-deduction-input" />
    </label>
    <label className="block col-span-2 sm:col-span-1">
      <span className="font-heading text-[11px] uppercase tracking-wider text-white/50">This amount is</span>
      <select value={mode} onChange={e => setMode(e.target.value)} className={`${selectCls} mt-1 w-full`} data-testid="icm-deduction-mode">
        <option value="winner">Played for — goes to 1st place</option>
        <option value="off_top">Taken off the pool</option>
      </select>
    </label>
    <label className="block">
      <span className="font-heading text-[11px] uppercase tracking-wider text-white/50">Round to</span>
      <select value={roundTo} onChange={e => setRoundTo(Number(e.target.value))} className={`${selectCls} mt-1 w-full`} data-testid="icm-round-select">
        {ROUND_UNITS.map(u => <option key={u} value={u}>{currency} {fmt(u)}</option>)}
      </select>
    </label>
    <label className="block">
      <span className="font-heading text-[11px] uppercase tracking-wider text-white/50">Currency</span>
      <select value={currency} onChange={e => setCurrency(e.target.value)} className={`${selectCls} mt-1 w-full`} data-testid="icm-currency-select">
        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </label>
  </div>
);

const Stat = ({ label, value, testid, cls, currency }) => (
  <div className="bg-black/40 rounded-lg px-3 py-2.5" data-testid={testid}>
    <p className="text-[10px] sm:text-[11px] uppercase tracking-wider text-white/40 font-heading">{label}</p>
    <p className={`font-mono text-base sm:text-lg ${cls}`}>{currency} {fmt(value)}</p>
  </div>
);

const Results = ({ result, currency }) => {
  if (!result) return <p className="text-white/40 text-sm" data-testid="icm-results-empty">Enter chip counts and payouts, then press Calculate — ICM and chip-chop amounts appear in the deal sheet above.</p>;
  return (
    <div className="space-y-3" data-testid="icm-results">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        <Stat label="Remaining prize pool" value={result.total_pool} testid="icm-stat-total" cls="text-white" currency={currency} />
        <Stat label={result.deduction_mode === 'winner' ? 'Playing amount for 1st' : 'Deducted from pool'} value={result.deduction} testid="icm-stat-deduction" cls="text-amber-300" currency={currency} />
        <Stat label="Pool to split" value={result.split_pool} testid="icm-stat-split" cls="text-secondary-main" currency={currency} />
        <Stat label="Rounding unit" value={result.round_to} testid="icm-stat-round" cls="text-white/70" currency={currency} />
      </div>
      {result.winner_bonus > 0 && <p className="text-amber-300 text-xs" data-testid="icm-winner-bonus">The playing amount {currency} {fmt(result.winner_bonus)} goes to whoever finishes 1st, on top of their agreed share.</p>}
    </div>
  );
};

const DealHistory = ({ deals, onLoad, onDelete, filterLabel }) => (
  <div className="bg-background-surface border border-white/[0.06] rounded-xl p-4 sm:p-5" data-testid="icm-history">
    <div className="flex items-center gap-2 mb-3">
      <History className="w-4 h-4 text-primary-main" />
      <h3 className="font-heading text-sm tracking-wider uppercase text-primary-main">Saved deals</h3>
      <span className="text-[11px] text-white/35">{filterLabel}</span>
    </div>
    {deals.length === 0
      ? <p className="text-white/40 text-sm" data-testid="icm-history-empty">No saved deals yet. Press "Save deal" once the numbers are agreed.</p>
      : <ul className="divide-y divide-white/[0.05]">
          {deals.map(d => (
            <li key={d.id} className="py-2.5 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`icm-deal-${d.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{d.event_name || 'Manual deal'} <span className="text-white/40">· {d.players} players · {d.deduction_mode === 'winner' ? 'winner bonus' : 'off top'} {fmt(d.deduction)}</span></p>
                <p className="text-[11px] text-white/40">{new Date(d.created_at).toLocaleString('en-GB', { timeZone: 'Asia/Seoul' })} KST · pool {d.currency} {fmt(d.total_pool)} → split {d.currency} {fmt(d.split_pool)}{d.note ? ` · ${d.note}` : ''}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => onLoad(d.id)} className="border border-white/10 text-white/80 h-9" data-testid={`icm-deal-load-${d.id}`}><FolderOpen className="w-4 h-4 mr-1" /> Open</Button>
                <Button variant="ghost" size="sm" onClick={() => onDelete(d.id)} className="border border-white/10 text-white/50 hover:text-red-400 h-9" data-testid={`icm-deal-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </li>
          ))}
        </ul>}
  </div>
);

export const IcmTab = ({ token, fullscreen = false }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [date, setDate] = useState(kstToday);
  const [events, setEvents] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [eventId, setEventId] = useState('');
  const [eventInfo, setEventInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([{ name: 'Player 1', chips: 0 }, { name: 'Player 2', chips: 0 }, { name: 'Player 3', chips: 0 }]);
  const [payouts, setPayouts] = useState([0, 0, 0]);
  const [deduction, setDeduction] = useState(0);
  const [mode, setMode] = useState('winner');
  const [roundTo, setRoundTo] = useState(1000);
  const [currency, setCurrency] = useState('₩');
  const [result, setResult] = useState(null);
  const [deals, setDeals] = useState([]);
  const [branding, setBranding] = useState({ logo_path: '', festival_name: '' });
  const [brandBusy, setBrandBusy] = useState(false);
  const [brandLoaded, setBrandLoaded] = useState(false);
  const brandApplied = useRef(false);
  const [method, setMethod] = useState('icm'); // icm | chop | both — what the sheet & PDF show
  const [pdfBusy, setPdfBusy] = useState(false);
  const timer = useRef(null);
  const pendingEvent = useRef(null);
  const eventsSeq = useRef(0);
  const resultsRef = useRef(null);
  const [calcBusy, setCalcBusy] = useState(false);

  const loadDeals = useCallback(() => {
    axios.get(`${API}/admin/icm/deals`, { params: eventId ? { pokerlens_id: eventId } : {}, headers }).then(r => setDeals(r.data)).catch(e => console.error('icm deals', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const fetchEvents = useCallback(async (force = false) => {
    const seq = ++eventsSeq.current;
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/admin/icm/events`, { params: { date, refresh: force }, headers });
      if (seq !== eventsSeq.current) return null; // a newer date was requested meanwhile
      setEvents(data);
      if (force) toast[data.length ? 'success' : 'info'](data.length ? `${data.length} event(s) on PokerLens for ${date}` : `PokerLens has no events for ${date} yet — check the TD software sync`);
      return data;
    } catch (e) { console.error('icm events', e); return []; }
    finally { setRefreshing(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    fetchEvents().then((data) => {
      if (data === null) return; // superseded by a newer request
      setEventId(pendingEvent.current ?? '');
      if (pendingEvent.current == null) setEventInfo(null);
      pendingEvent.current = null;
    });
  }, [fetchEvents]);

  useEffect(() => { loadDeals(); }, [loadDeals]);

  useEffect(() => {
    axios.get(`${API}/admin/icm/settings`, { headers })
      .then(r => { if (brandApplied.current) return; brandApplied.current = true; setBranding(r.data); })
      .catch(e => console.error('icm settings', e))
      .finally(() => setBrandLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveBranding = async (next) => {
    setBrandBusy(true);
    try { const { data } = await axios.put(`${API}/admin/icm/settings`, next, { headers }); setBranding(data); toast.success('Sheet branding saved'); }
    catch (e) { toast.error('Could not save branding'); }
    finally { setBrandBusy(false); }
  };

  const uploadLogo = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setBrandBusy(true);
      try {
        const fd = new FormData(); fd.append('file', file);
        const { data } = await axios.post(`${API}/admin/upload`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
        await saveBranding({ ...branding, logo_path: data.path });
      } catch (err) { toast.error('Logo upload failed'); setBrandBusy(false); }
    };
    input.click();
  };

  const loadEvent = useCallback(async (id = eventId) => {
    if (!id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/icm/event/${id}`, { headers });
      setEventInfo(data);
      if (data.players.length) setPlayers(data.players.map(p => ({ name: p.name, chips: p.chips })));
      else toast.info('PokerLens has no chip counts for this event yet — the TD needs to enter stacks (or enter players manually).');
      if (data.payouts.length) setPayouts(data.payouts.map(p => p.amount));
      setCurrency(data.currency || '₩');
      if (data.players.length || data.payouts.length) toast.success(`Loaded ${data.players.length} players / ${data.payouts.length} payouts${data.payouts_estimated ? ' (payout split estimated)' : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load from PokerLens');
    } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // Picking an event auto-loads its players + chips + payouts from PokerLens.
  const pickEvent = (id) => { setEventId(id); if (id) loadEvent(id); else setEventInfo(null); };

  const ready = players.length > 0 && players.some(p => num(p.chips) > 0) && payouts.some(p => p > 0);

  const runCalc = useCallback(async () => {
    const { data } = await axios.post(`${API}/admin/icm/calc`, { players, payouts, deduction, deduction_mode: mode, round_to: roundTo }, { headers });
    setResult(data);
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, payouts, deduction, mode, roundTo]);

  // Live preview: recalculates shortly after any number changes.
  useEffect(() => {
    if (!ready) { setResult(null); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { runCalc().catch(e => toast.error(e.response?.data?.detail || 'Calculation failed')); }, 250);
    return () => clearTimeout(timer.current);
  }, [ready, runCalc]);

  const calculateNow = async () => {
    if (!ready) { toast.error(players.some(p => num(p.chips) > 0) ? 'Enter at least one payout amount' : 'Enter the players\' chip counts first'); return; }
    setCalcBusy(true);
    try {
      await runCalc();
      toast.success('Deal calculated');
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    } catch (e) { toast.error(e.response?.data?.detail || 'Calculation failed'); }
    finally { setCalcBusy(false); }
  };

  const dealPayload = (note = '') => {
    const ev = events.find(e => e.pokerlens_id === eventId);
    return { date, pokerlens_id: eventId, event_name: ev ? eventLabel(ev) : (eventInfo?.name || ''), currency,
      festival: branding.festival_name || eventInfo?.festival || '', buyin: eventInfo?.buyin || {},
      players, payouts, deduction, deduction_mode: mode, round_to: roundTo, result, note };
  };

  const exportPdf = async () => {
    if (!result) return;
    setPdfBusy(true);
    try {
      const res = await axios.post(`${API}/admin/icm/pdf`, { ...dealPayload(), agreed: method === 'chop' ? 'chop' : 'icm', show: method }, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = Object.assign(document.createElement('a'), { href: url, download: `deal-${date}.pdf`, target: '_blank' });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('PDF ready — print it and collect signatures');
    } catch (e) { toast.error('Could not create PDF'); }
    finally { setPdfBusy(false); }
  };

  const saveDeal = async () => {
    if (!result) return;
    const note = window.prompt('Note for this deal (optional):', '') ?? '';
    try {
      await axios.post(`${API}/admin/icm/deals`, dealPayload(note), { headers });
      toast.success('Deal saved to history');
      loadDeals();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save deal'); }
  };

  const openDeal = async (id) => {
    try {
      const { data } = await axios.get(`${API}/admin/icm/deals/${id}`, { headers });
      if (data.date && data.date !== date) { pendingEvent.current = data.pokerlens_id || ''; setDate(data.date); }
      else setEventId(data.pokerlens_id || '');
      setPlayers(data.players); setPayouts(data.payouts); setDeduction(data.deduction);
      setMode(data.deduction_mode); setRoundTo(data.round_to); setCurrency(data.currency || '₩');
      setEventInfo(data.event_name ? { name: data.event_name, status: 'saved deal', total_players_left: data.players.length, total_payout_amount: data.result?.total_pool || 0, festival: data.festival || '', buyin: data.buyin || {}, payouts: data.payouts || [] } : null);
      toast.success('Deal loaded — numbers are editable again');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toast.error('Could not open deal'); }
  };

  const deleteDeal = async (id) => {
    if (!window.confirm('Delete this saved deal?')) return;
    try { await axios.delete(`${API}/admin/icm/deals/${id}`, { headers }); toast.success('Deal deleted'); loadDeals(); }
    catch (e) { toast.error('Could not delete deal'); }
  };

  const exportCsv = () => {
    if (!result) return;
    const lines = [['Player', 'Chips', 'Chip %', 'ICM', 'Chip chop'], ...result.rows.map(r => [r.name, r.chips, r.chip_pct, r.icm, r.chop])];
    const blob = new Blob([lines.map(l => l.join(',')).join('\n')], { type: 'text/csv' });
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(blob), download: `icm-deal-${date}.csv` });
    a.click();
  };

  const selectedEvent = events.find(e => e.pokerlens_id === eventId);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="icm-tab">
      <Toaster theme="dark" richColors position="top-center" />
      <div className="bg-background-surface border border-white/[0.06] rounded-xl p-4 sm:p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-sm tracking-wider uppercase text-primary-main">ICM deal calculator</h3>
            <p className="text-[11px] text-white/35 mt-1 hidden sm:block">Pick the event → players, chips and unpaid places load from PokerLens → choose ICM or by chip count → Calculate → PDF to sign.</p>
          </div>
          {!fullscreen && (
            <a href="/admin/icm" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-heading tracking-wider uppercase text-secondary-main hover:underline" data-testid="icm-fullscreen-link">
              <Maximize2 className="w-3.5 h-3.5" /> Open for tablet / phone
            </a>
          )}
        </div>
        <EventPicker date={date} setDate={setDate} events={events} eventId={eventId} onPick={pickEvent} onLoad={() => loadEvent()} loading={loading} onRefreshEvents={() => fetchEvents(true)} refreshing={refreshing} />
        {eventInfo && (
          <p className="text-xs text-white/50" data-testid="icm-event-info">
            {eventInfo.festival && <span className="text-primary-main font-heading tracking-wider uppercase mr-2" data-testid="icm-festival-name">{eventInfo.festival}</span>}
            <span className="text-white">{eventInfo.name}</span> · {eventInfo.status} · {eventInfo.total_players_left} players left · total prize pool {currency} {fmt(eventInfo.total_payout_amount)}
            {eventInfo.buyin?.total > 0 && <> · <span data-testid="icm-buyin-line">buy-in <span className="text-white">{currency} {fmt(eventInfo.buyin.total)}</span> = entry {fmt(eventInfo.buyin.entry)} + admin fee {fmt(eventInfo.buyin.fee)}{eventInfo.buyin.bounty > 0 ? ` + bounty ${fmt(eventInfo.buyin.bounty)}` : ''}</span></>}
            {eventInfo.unassigned_amount != null && <> · <span className="text-secondary-main">unassigned {currency} {fmt(eventInfo.unassigned_amount)}</span>{eventInfo.assigned_places ? ` (${eventInfo.assigned_places} place${eventInfo.assigned_places > 1 ? 's' : ''} already paid, excluded)` : ''}</>}
          </p>
        )}
        <DealTable players={players} setPlayers={setPlayers} payouts={payouts} setPayouts={setPayouts} currency={currency} result={result} method={method} setMethod={setMethod}
          hint={eventInfo ? (eventInfo.payouts_estimated
            ? `PokerLens only publishes the unassigned total (${currency} ${fmt(eventInfo.unassigned_amount)} over ${eventInfo.payouts?.length} place${eventInfo.payouts?.length > 1 ? 's' : ''}), not each amount — a standard split is pre-filled. Adjust the payout cells to match your payout sheet.`
            : eventInfo.payouts?.length === 0 ? (eventInfo.assigned_places > 0
              ? 'All payout places are already assigned on PokerLens — nothing left to deal.'
              : `PokerLens has not published a payout table for this event yet (prize pool ${currency} ${fmt(eventInfo.total_payout_amount)}). Type the remaining places, or publish payouts in PokerLens and press "Load from PokerLens".`) : null) : null} />
        <DealSettings deduction={deduction} setDeduction={setDeduction} mode={mode} setMode={setMode} roundTo={roundTo} setRoundTo={setRoundTo} currency={currency} setCurrency={setCurrency} />
        <Button onClick={calculateNow} disabled={calcBusy} className="w-full h-12 bg-primary-main text-black font-heading tracking-wider uppercase text-base hover:bg-primary-main/90" data-testid="icm-calculate-btn">
          <Calculator className="w-5 h-5 mr-2" /> {calcBusy ? 'Calculating…' : `Calculate deal${method === 'icm' ? ' (ICM)' : method === 'chop' ? ' (by chip count)' : ' (ICM & chip count)'}`}
        </Button>
      </div>

      <div ref={resultsRef} className="bg-background-surface border border-white/[0.06] rounded-xl p-4 sm:p-5 scroll-mt-16">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="font-heading text-sm tracking-wider uppercase text-primary-main">Deal</h3>
          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={exportPdf} disabled={!result || pdfBusy} className="bg-secondary-main text-white h-10" data-testid="icm-pdf-btn"><FileText className="w-4 h-4 mr-1.5" /> {pdfBusy ? 'Creating…' : 'PDF to sign'}</Button>
            <Button onClick={saveDeal} disabled={!result} className="bg-primary-main text-black h-10" data-testid="icm-save-deal-btn"><Save className="w-4 h-4 mr-1.5" /> Save deal</Button>
            <Button variant="ghost" onClick={exportCsv} disabled={!result} className="border border-white/10 text-white/70 h-10" data-testid="icm-export-btn"><Download className="w-4 h-4 mr-1.5" /> CSV</Button>
          </div>
        </div>
        <Results result={result} currency={currency} />
      </div>

      <div className="bg-background-surface border border-white/[0.06] rounded-xl p-4 sm:p-5" data-testid="icm-branding">
        <h3 className="font-heading text-sm tracking-wider uppercase text-primary-main mb-3">Deal sheet branding</h3>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-28 h-16 rounded-md bg-black/40 border border-white/10 flex items-center justify-center overflow-hidden">
              {branding.logo_path ? <img src={`${API}/files/${branding.logo_path}`} alt="Event logo" className="max-w-full max-h-full object-contain" data-testid="icm-logo-preview" /> : <span className="text-[11px] text-white/30">No logo</span>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Button variant="ghost" size="sm" onClick={uploadLogo} disabled={brandBusy} className="border border-white/10 text-white/80 h-9" data-testid="icm-logo-upload-btn">{brandBusy ? 'Saving…' : branding.logo_path ? 'Change logo' : 'Upload event logo'}</Button>
              {branding.logo_path && <button onClick={() => saveBranding({ ...branding, logo_path: '' })} className="text-[11px] text-white/40 hover:text-red-400 text-left" data-testid="icm-logo-remove-btn">Remove logo</button>}
            </div>
          </div>
          <label className="block flex-1">
            <span className="font-heading text-[11px] uppercase tracking-wider text-white/50">Festival name on the sheet</span>
            <div className="flex gap-2 mt-1">
              <Input value={branding.festival_name} disabled={!brandLoaded} onChange={e => setBranding({ ...branding, festival_name: e.target.value })} placeholder={brandLoaded ? (eventInfo?.festival || 'e.g. Red Dragon Poker Tour Jeju 2026') : 'Loading…'} className={inputCls} data-testid="icm-festival-input" />
              <Button onClick={() => saveBranding(branding)} disabled={brandBusy || !brandLoaded} className="bg-secondary-main text-white h-10" data-testid="icm-festival-save-btn">Save</Button>
            </div>
            <p className="text-[11px] text-white/35 mt-1">Leave empty to use the festival detected from PokerLens for the selected event.</p>
          </label>
        </div>
      </div>

      <DealHistory deals={deals} onLoad={openDeal} onDelete={deleteDeal} filterLabel={selectedEvent ? `for ${eventLabel(selectedEvent)}` : 'all events, newest first'} />
    </div>
  );
};
